import type React from "react";
import { useMemo, useState } from "react";

import { ExternalLinkModal } from "simplydo/react/components/ExternalLinkModal";

import { RemirrorEditor, type RemirrorProps } from "./Remirror";
import { EditMenu, type ExtraMenuItem, type MenuCommands } from "./ui/EditMenu";
import { FloatingLinkToolbar } from "./ui/FloatingLinkToolbar";
import { FocusProvider } from "./ui/FocusProvider";
import Mentions from "./ui/Mentions";

export type RemirrorMenuItem = ExtraMenuItem & {
  // Back-compat: historically `commands` was loosely typed.
  onClick?: (attrs: {
    name: string;
    commands: MenuCommands;
    selectedText: string;
    isActive: boolean;
  }) => void;
};

export type BasicRichTextProps = {
  extraMenuItems?: Array<RemirrorMenuItem>;
  HelpText?: React.ReactNode;
  stickyMenu?: boolean;
  stickyMenuOffset?: number;

  hideMenu?: boolean;
  autoHideMenu?: boolean;
  historyEnabled?: boolean;

  // These are passed through from `RichText` and used by `Mentions`.
  mentions?: Record<string, unknown[]>;
  onSuggest?: (kind: string[], query: string) => void;
  onMentionSelect?: (mention: unknown) => void;
} & RemirrorProps;

export const BasicRichText = ({
  settings,
  options = [],
  readOnly = false,
  hideMenu,
  mentions,
  extraMenuItems,
  stickyMenu,
  stickyMenuOffset,
  historyEnabled,
  autoHideMenu = true,
  onSuggest,
  onMentionSelect,
  ...otherProps
}: BasicRichTextProps) => {
  const [followExternalLink, setFollowExternalLink] = useState<string | null>(
    null,
  );
  const enabledExtensions = useMemo(
    () => options.map((o) => o.name),
    [options],
  );

  return (
    <>
      <ExternalLinkModal
        open={followExternalLink !== null}
        href={followExternalLink || ""}
        onClose={() => {
          setFollowExternalLink(null);
        }}
        onFollow={() => {
          setFollowExternalLink(null);
        }}
      />
      <RemirrorEditor
        options={options}
        readOnly={!!readOnly}
        settings={{
          ...settings,
          onExternalLink: (href) => {
            if (settings?.onExternalLink) {
              settings.onExternalLink(href);
              return;
            }
            setFollowExternalLink(href);
          },
        }}
        {...otherProps}
      >
        <FocusProvider>
          {!readOnly && !hideMenu ? (
            <EditMenu
              extraMenuItems={extraMenuItems}
              sticky={!!stickyMenu}
              stickyOffset={stickyMenuOffset ?? 0}
              historyEnabled={!!historyEnabled}
              autoHideMenu={!!autoHideMenu}
            />
          ) : null}
          {!readOnly && enabledExtensions.includes("mention") ? (
            <Mentions
              mentions={(mentions || {}) as any}
              options={options}
              onMentionSelect={(onMentionSelect || (() => {})) as any}
              onSuggest={(onSuggest || (() => {})) as any}
            />
          ) : null}
          {!readOnly && !hideMenu && enabledExtensions.includes("link") ? (
            <FloatingLinkToolbar />
          ) : null}
        </FocusProvider>
      </RemirrorEditor>
    </>
  );
};
